import type { PerformedSet, ReadinessCheckin, WorkoutLog } from '@cadencia/contracts';
import { findExercise, formatKg, generateWarmup, requireExercise } from '@cadencia/domain';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Descanso } from '../../components/Descanso.js';
import { IconoCerrar, IconoPunta, IconoSustituir } from '../../components/iconos.js';
import { guardarDescanso, leerDescanso } from '../../data/descansoActivo.js';
import { prepararSonido } from '../../platform/sonido.js';
import {
  avisarFinDeDescanso,
  cancelarFinDeDescanso,
  programarFinDeDescanso,
} from '../../data/services/notificationService.js';
import { Boton, BotonEnlace, Cabecera, Estado } from '../../components/ui.js';
import { useAppData } from '../../data/DataProvider.js';
import { db } from '../../data/db.js';
import { listAvailableLoads } from '../../data/repositories/gymRepo.js';
import { COMO_SE_HACE, seriePermitidaHoy } from '@cadencia/domain';
import { getCheckinForDate } from '../../data/repositories/readinessRepo.js';
import {
  getSessionDetail,
  listWarmups,
  markWarmupCompleted,
  saveWarmups,
  type SessionDetail,
} from '../../data/repositories/trainingRepo.js';
import {
  listPerformedSets,
  logPerformedSet,
  logRestEvent,
  startWorkout,
} from '../../data/repositories/workoutRepo.js';
import {
  persistRecommendation,
  recommendForSet,
  type SetRecommendation,
} from '../../data/services/recommendationService.js';
import { currentSafetyState, type SafetyState } from '../../data/services/safetyService.js';
import { todayLocal } from '../../lib/dates.js';
import { logger } from '../../lib/logger.js';
import {
  camposConProblema,
  esErrorDeValidacion,
  LIMITES_SERIE,
  traducirErrorDeValidacion,
  validarSerie,
} from '../../lib/validacionSerie.js';
import { keepScreenAwake, releaseScreenAwake } from '../../platform/capabilities.js';
import { GuiaPrimeraSesion } from './GuiaPrimeraSesion.js';
import { hasAnyPerformedSet } from '../../data/repositories/workoutRepo.js';
import { PorQuePanel } from './PorQuePanel.js';
import { SustituirEjercicio } from './SustituirEjercicio.js';
import './entrenar.css';
import { detalleDeError, mensajeDeError } from '../../lib/errores.js';

interface Posicion {
  indiceEjercicio: number;
  indiceSerie: number;
}

/**
 * Primera serie prescrita que todavía no se ha registrado.
 *
 * Salta las que hoy no tocan. Hoy eso es solo el drop set con el readiness por
 * los suelos (§11), y la regla la pone el dominio en `seriePermitidaHoy`: la
 * tecnica se decidio al materializar la semana, dias antes, cuando el readiness
 * del dia no existia. Sin este filtro, un drop planificado el lunes se haria el
 * jueves con el readiness hundido, que es justo lo que el §11 prohibe.
 */
function siguientePosicion(
  detalle: SessionDetail,
  hechas: PerformedSet[],
  readiness: number | null,
): Posicion | null {
  const hechasPorEjercicio = new Map<string, Set<number>>();
  for (const set of hechas) {
    const clave = set.sessionExerciseId ?? '';
    const actual = hechasPorEjercicio.get(clave) ?? new Set<number>();
    actual.add(set.setIndex);
    hechasPorEjercicio.set(clave, actual);
  }

  for (const [indiceEjercicio, item] of detalle.exercises.entries()) {
    const hechasAqui = hechasPorEjercicio.get(item.sessionExercise.id) ?? new Set<number>();
    for (const [indiceSerie, prescripcion] of item.prescriptions.entries()) {
      if (hechasAqui.has(prescripcion.setIndex)) continue;
      if (!seriePermitidaHoy(prescripcion.setType, readiness).permitida) continue;
      return { indiceEjercicio, indiceSerie };
    }
  }
  return null;
}

export function SesionActivaPage(): ReactNode {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { ctx, ready, refreshPending } = useAppData();
  const navigate = useNavigate();

  const [detalle, setDetalle] = useState<SessionDetail | null | undefined>(undefined);
  const [workoutLogId, setWorkoutLogId] = useState<string | null>(null);
  const [checkin, setCheckin] = useState<ReadinessCheckin | null>(null);
  const [seguridad, setSeguridad] = useState<SafetyState | null>(null);
  const [recomendacion, setRecomendacion] = useState<SetRecommendation | null>(null);
  const [carga, setCarga] = useState('');
  const [reps, setReps] = useState('');
  const [rir, setRir] = useState('');
  const [descanso, setDescanso] = useState<{ segundos: number; desde: number; setId: string } | null>(
    null,
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calentamientoAbierto, setCalentamientoAbierto] = useState(false);
  // Los dos paneles de la fila de acciones secundarias. Uno a la vez: son
  // hermanos justo debajo de la fila y abiertos juntos empujan «Guardar serie»
  // fuera de la pantalla, que es la unica accion que no puede costar un scroll.
  const [porQueAbierto, setPorQueAbierto] = useState(false);
  const [sustituyendo, setSustituyendo] = useState(false);
  // Aviso propio del calentamiento: su boton esta muy por encima del `error`
  // general, y un mensaje que hay que ir a buscar no lo lee nadie.
  const [errorCalentamiento, setErrorCalentamiento] = useState<string | null>(null);
  const [cargasDisponibles, setCargasDisponibles] = useState<number[]>([]);
  const iniciado = useRef(false);
  // Serie para la que ya se han prellenado los campos. Sin esto, una
  // recomendacion que llega tarde pisaria lo que el usuario acaba de teclear.
  const prellenadoPara = useRef<string | null>(null);

  // `false` = mirado y nunca ha registrado una serie; `undefined` = sin mirar.
  // La guía solo sale con `false`: mejor no enseñarla un instante que enseñarla
  // de más a quien lleva meses entrenando.
  const yaHaEntrenado = useLiveQuery(
    async () => (ctx ? hasAnyPerformedSet(ctx.userId) : undefined),
    [ctx],
  );

  const hechasConsulta = useLiveQuery(
    async () => (workoutLogId ? listPerformedSets(workoutLogId) : []),
    [workoutLogId],
  );
  // Referencia estable: sin esto, cada render crearia un array nuevo y
  // recalcularia posicion, recomendacion y calentamiento sin motivo.
  const hechas = useMemo(() => hechasConsulta ?? [], [hechasConsulta]);

  // Arranque: carga la sesión, el check-in y crea (o recupera) el registro.
  useEffect(() => {
    if (!ctx || !sessionId || iniciado.current) return;
    iniciado.current = true;

    void (async () => {
      try {
        const cargado = await getSessionDetail(sessionId);
        setDetalle(cargado);
        if (!cargado) return;

        const readiness = (await getCheckinForDate(ctx.userId, todayLocal())) ?? null;
        setCheckin(readiness);
        // Se consulta ANTES de calcular nada: con una bandera roja no se
        // prescribe (docs/SECURITY_PRIVACY.md §6.1).
        setSeguridad(await currentSafetyState(ctx.userId));

        const log = await startWorkout(ctx, cargado, readiness?.id ?? null);
        setWorkoutLogId(log.id);
        await refreshPending();
      } catch (caught) {
        // El otro sitio de esta misma pantalla donde se colaba el JSON de Zod:
        // `startWorkout` valida el parte contra `workoutLogSchema`, y una fila
        // que no encaje aterrizaba aquí en crudo. No se traduce por campo —lo
        // que falla es un dato GUARDADO, no lo que el usuario acaba de
        // escribir— pero tampoco se vuelca el esquema en pantalla.
        // Al registro va el DETALLE —el JSON de Zod con su campo y su
        // limite—, que es justo lo que hace falta para arreglarlo; a la
        // pantalla, la frase. Centralizar los mensajes casi se lleva por
        // delante tambien el diagnostico.
        const detalle = detalleDeError(caught);
        logger.error('db', 'SESSION_START_FAILED', 'No se pudo preparar la sesión', {
          mensaje: detalle,
        });
        setError(
          esErrorDeValidacion(caught)
            ? 'No he podido preparar la sesión: hay un dato guardado que no encaja con lo que espera la aplicación.'
            : `No he podido preparar la sesión: ${detalle}`,
        );
      }
    })();
  }, [ctx, sessionId, refreshPending]);

  /**
   * Vuelve a leer la sesion desde Dexie.
   *
   * `detalle` se carga UNA vez y `iniciado` cierra la puerta, y eso es
   * deliberado: la pantalla no puede rehacerse sola mientras se entrena.
   * Sustituir un ejercicio es la excepcion, y la pide el usuario. Sin esta
   * relectura el cambio se escribe en la base de datos y la pantalla sigue
   * pintando —y registrando— el ejercicio de antes.
   */
  const recargarDetalle = useCallback(async (): Promise<void> => {
    if (!sessionId) return;
    try {
      setDetalle(await getSessionDetail(sessionId));
    } catch (caught) {
      // El motivo VIAJA con el resultado: el cambio SI se ha guardado, asi que
      // lo que hay que decir no es «no se ha podido» sino que la pantalla se ha
      // quedado atras y hay que volver a entrar.
      logger.error('db', 'SESSION_RELOAD_FAILED', 'No se pudo recargar la sesión', {
        mensaje: detalleDeError(caught),
      });
      setError(
        `El cambio está guardado, pero no he podido recargar la sesión: ${mensajeDeError(caught)}. Sal y vuelve a entrar.`,
      );
    }
  }, [sessionId]);

  // Mantener la pantalla encendida durante el entrenamiento (requisito §74).
  useEffect(() => {
    void keepScreenAwake();
    return () => {
      void releaseScreenAwake();
    };
  }, []);

  const posicion = useMemo(
    () => (detalle ? siguientePosicion(detalle, hechas, checkin?.readinessScore ?? null) : null),
    [detalle, hechas, checkin],
  );

  const actual = useMemo(() => {
    if (!detalle || !posicion) return null;
    const item = detalle.exercises[posicion.indiceEjercicio];
    const prescripcion = item?.prescriptions[posicion.indiceSerie];
    if (!item || !prescripcion) return null;
    return { item, prescripcion };
  }, [detalle, posicion]);

  /** Todo lo registrado hoy en este hueco, sea del ejercicio que sea. */
  const seriesDelHuecoHoy = useMemo(
    () =>
      actual
        ? hechas
            .filter((set) => set.sessionExerciseId === actual.item.sessionExercise.id)
            .sort((a, b) => a.setIndex - b.setIndex)
        : [],
    [hechas, actual],
  );

  /**
   * Las del ejercicio que hay AHORA en el hueco, que no siempre son las mismas.
   *
   * El filtro por `exerciseSlug` es lo que hace seguro sustituir a mitad de
   * ejercicio. Con el hueco como unica clave, las series del ejercicio de antes
   * entraban en `performedToday` y el ajuste intra-sesion del motor parte de la
   * carga de la ULTIMA serie, acotada a ±10 % de la primera: el press de banca
   * a 72,5 kg habria fijado la carga de la maquina que lo sustituye. No es un
   * error ni un aviso, es un numero plausible imposible de poner.
   *
   * Quien cuenta la fatiga acumulada sigue siendo `hechas` entero, y ahi no
   * sobra nada: esas series se hicieron y cargaron sus musculos. El dominio las
   * atribuye por el slug de CADA serie (`sameMuscleSetsBefore`).
   */
  /**
   * La serie que toca, con el ejercicio DENTRO de la clave.
   *
   * Los dos candados de esta pantalla —prellenar los campos y persistir las
   * aproximaciones— se cerraban con el `id` de la prescripcion y el del hueco,
   * y ninguno de los dos cambia al sustituir: la prescripcion sigue siendo la
   * serie 3 de ese hueco. O sea que tras un cambio los campos se quedaban con
   * la carga del ejercicio de antes y el calentamiento no se recalculaba. Con
   * el slug en la clave, cambiar de ejercicio abre los dos candados y no hace
   * falta reiniciarlos a mano desde ningun sitio.
   */
  const claveDeSerie = useMemo(
    () =>
      actual === null
        ? null
        : `${actual.prescripcion.id}|${actual.item.sessionExercise.exerciseSlug}`,
    [actual],
  );

  const seriesDeHoyEnEsteEjercicio = useMemo(
    () =>
      actual
        ? seriesDelHuecoHoy.filter(
            (set) => set.exerciseSlug === actual.item.sessionExercise.exerciseSlug,
          )
        : [],
    [seriesDelHuecoHoy, actual],
  );

  // Recomendación de carga para la serie actual.
  useEffect(() => {
    if (!ctx || !actual || !detalle || !workoutLogId) return;
    let cancelado = false;

    void (async () => {
      try {
        const equipmentId = actual.item.sessionExercise.equipmentId;
        const cargas =
          equipmentId === null ? [] : (await listAvailableLoads(equipmentId)).map((c) => c.loadKg);
        if (!cancelado) setCargasDisponibles(cargas);

        const recomendada = await recommendForSet({
          userId: ctx.userId,
          sessionExercise: actual.item.sessionExercise,
          prescription: actual.prescripcion,
          totalSets: actual.item.prescriptions.length,
          performedToday: seriesDeHoyEnEsteEjercicio,
          // Las series de hoy TAL CUAL: cuales cuentan como fatiga para este
          // ejercicio lo decide el dominio, no esta pantalla (TRAIN-001).
          setsDoneToday: hechas,
          readiness: checkin,
          // La semana y el bloque de verdad. Aqui habia dos literales
          // —`'reintroduction'` y `true`— que le contaban al motor una semana
          // que no era: acumulacion, intensificacion y descarga no llegaban
          // nunca, y la rama de descarga era inalcanzable desde la aplicacion
          // (BUG-002).
          week: detalle.week,
          block: detalle.block,
          redFlags: seguridad?.flags ?? [],
        });
        if (cancelado) return;

        setRecomendacion(recomendada);

        // Solo se prellena una vez por serie: lo que el usuario haya escrito
        // manda sobre una recomendacion que llegue despues.
        if (prellenadoPara.current !== claveDeSerie) {
          prellenadoPara.current = claveDeSerie;
          setCarga(recomendada.loadKg === null ? '' : String(recomendada.loadKg));
          setReps(String(recomendada.targetReps));
          setRir(String(recomendada.targetRir));
        }
      } catch (caught) {
        // Sin recomendacion se puede entrenar igual: se avisa y se deja
        // introducir la carga a mano.
        const detalle = detalleDeError(caught);
        const mensaje = mensajeDeError(caught);
        logger.error('engine', 'RECOMMENDATION_FAILED', 'No se pudo calcular la carga', {
          mensaje: detalle,
        });
        if (!cancelado && prellenadoPara.current !== claveDeSerie) {
          prellenadoPara.current = claveDeSerie;
          setError(`No he podido calcular la carga recomendada: ${mensaje}`);
          setReps(
            String(
              Math.round(
                (actual.prescripcion.targetRepsMin + actual.prescripcion.targetRepsMax) / 2,
              ),
            ),
          );
          setRir(String(actual.prescripcion.targetRir));
        }
      }
    })();

    return () => {
      cancelado = true;
    };
    // `hechas` es referencia estable (useMemo) y cambia con cada serie: sirve de
    // disparador y ademas es lo que ahora se le pasa al motor.
  }, [
    ctx,
    actual,
    claveDeSerie,
    detalle,
    workoutLogId,
    checkin,
    seguridad,
    hechas,
    seriesDeHoyEnEsteEjercicio,
  ]);

  const calentamientoCalculado = useMemo(() => {
    if (!actual || !recomendacion) return [];
    const ejercicio = requireExercise(actual.item.sessionExercise.exerciseSlug);
    if (actual.prescripcion.setIndex > 1) return [];

    return generateWarmup({
      workLoadKg: recomendacion.loadKg,
      e1rmKg: (recomendacion.inputsSnapshot['e1rmKg'] as number | undefined) ?? null,
      isFirstOfSession: actual.item.sessionExercise.orderIndex === 1,
      muscleAlreadyWarm: actual.item.sessionExercise.orderIndex > 2,
      isCompound: ejercicio.isCompound,
      availableLoads: cargasDisponibles,
    });
  }, [actual, recomendacion, cargasDisponibles]);

  // Las aproximaciones se PERSISTEN: viven en su propia tabla para que sea
  // estructuralmente imposible que contaminen el volumen efectivo (ADR-013).
  // Antes se calculaban solo en memoria y `warmup_prescriptions` quedaba vacia.
  const calentamientoGuardado = useLiveQuery(
    async () => (actual ? listWarmups(actual.item.sessionExercise.id) : []),
    [actual?.item.sessionExercise.id],
  );

  const persistidoPara = useRef<string | null>(null);
  useEffect(() => {
    if (!ctx || !actual || calentamientoCalculado.length === 0) return;
    if (calentamientoGuardado === undefined) return; // todavia cargando
    const hueco = actual.item.sessionExercise.id;
    // El candado lleva el ejercicio dentro, por lo mismo que `claveDeSerie`:
    // sustituir no cambia el id del hueco, asi que sin el slug las
    // aproximaciones del ejercicio anterior contarian como ya persistidas y las
    // del nuevo no se guardarian nunca.
    const clave = `${hueco}|${actual.item.sessionExercise.exerciseSlug}`;
    if (persistidoPara.current === clave) return;
    persistidoPara.current = clave;
    if (calentamientoGuardado.length > 0) return; // ya estaban

    void (async () => {
      try {
        await saveWarmups(ctx, hueco, calentamientoCalculado);
        await refreshPending();
      } catch (caught) {
        logger.error('engine', 'WARMUP_SAVE_FAILED', 'No se pudo guardar el calentamiento', {
          message: detalleDeError(caught),
        });
      }
    })();
  }, [ctx, actual, calentamientoCalculado, calentamientoGuardado, refreshPending]);

  /**
   * Filas que se pintan: las persistidas en cuanto existen (traen `id` y
   * `isCompleted`), y mientras tanto las recien calculadas, en solo lectura.
   */
  const calentamiento = useMemo(() => {
    if (calentamientoGuardado && calentamientoGuardado.length > 0) {
      return calentamientoGuardado.map((fila) => ({
        clave: fila.id,
        orderIndex: fila.orderIndex,
        loadKg: fila.loadKg,
        reps: fila.reps,
        completada: fila.isCompleted,
        fila,
      }));
    }
    return calentamientoCalculado.map((serie) => ({
      clave: `calc-${String(serie.orderIndex)}`,
      orderIndex: serie.orderIndex,
      loadKg: serie.loadKg,
      reps: serie.reps,
      completada: false,
      fila: null,
    }));
  }, [calentamientoGuardado, calentamientoCalculado]);

  const guardarSerie = useCallback(() => {
    if (!ctx || !actual || !workoutLogId || !detalle || guardando) return;

    const cargaNum = Number(carga.replace(',', '.'));
    const repsNum = Number(reps);
    const rirNum = Number(rir.replace(',', '.'));
    if (!Number.isFinite(cargaNum) || !Number.isFinite(repsNum) || !Number.isFinite(rirNum)) {
      setError('Revisa carga, repeticiones y RIR.');
      return;
    }
    // La primera vez con un ejercicio el motor NO propone carga a proposito, y
    // el campo llega vacio. Sin esta comprobacion, pulsar «Guardar serie» sin
    // escribir nada registraba una serie de 0 kg: `Number('')` es 0 y es finito.
    // Esa fila entra luego en el historial del que sale toda recomendacion
    // futura, asi que es dato corrupto en la raiz del sistema.
    if (cargaNum <= 0) {
      setError('Escribe la carga que has usado antes de guardar la serie.');
      return;
    }
    if (repsNum <= 0) {
      setError('Una serie registrada necesita al menos una repetición.');
      return;
    }
    // La misma trampa con el RIR, y peor: `Number('')` es 0, y un RIR vacío
    // entraba al historial como una serie AL FALLO. «No lo sé» no es «no me
    // quedaba ninguna» (encargo del 23/09/2026, §11).
    if (rir.trim() === '') {
      setError('Escribe el RIR: cuántas repeticiones más podrías haber hecho. Si dudas, estímalo; vacío no es cero.');
      return;
    }
    // Y ahora el contrato, antes de tocar la base de datos. Los limites no se
    // repiten aqui: `validarSerie` los saca de `performedSetSchema`, que es
    // quien manda. Lo que cambia es que el rechazo se cuenta en castellano en
    // vez de volcar el JSON de Zod en la pantalla (UX-001).
    const validacion = validarSerie({ loadKg: cargaNum, reps: repsNum, rir: rirNum });
    if (!validacion.ok) {
      logger.warn('ui', 'SET_INPUT_INVALID', 'Serie rechazada por el contrato', {
        campos: validacion.campos,
      });
      setError(validacion.mensaje);
      return;
    }

    setGuardando(true);
    setError(null);

    /*
     * El contexto de audio se abre AQUI, dentro del gesto que guarda la serie.
     *
     * Chrome —y el WebView de Android lo es— deja `suspended` cualquier
     * `AudioContext` creado sin un gesto detras, y de ahi no sale solo: el
     * pitido del fin del descanso no sonaria y no habria ni un error que lo
     * dijera. Este toque es el que arranca el descanso, asi que es el momento.
     */
    prepararSonido();

    void (async () => {
      const t0 = performance.now();
      try {
        const log = await ctxWorkoutLog(workoutLogId);
        if (!log) throw new Error('No se encuentra el registro de la sesión');

        const set = await logPerformedSet(ctx, {
          workoutLog: log,
          sessionExerciseId: actual.item.sessionExercise.id,
          prescription: actual.prescripcion,
          exerciseSlug: actual.item.sessionExercise.exerciseSlug,
          equipmentId: actual.item.sessionExercise.equipmentId,
          loadKg: cargaNum,
          reps: repsNum,
          rir: rirNum,
          recommendedLoadKg: recomendacion?.loadKg ?? null,
          restPlannedSeconds: actual.prescripcion.restSeconds,
        });

        const ms = performance.now() - t0;
        logger.debug('engine', 'SET_SAVED_LOCAL', 'Serie guardada en local', {
          ms: Math.round(ms),
        });

        // El descanso arranca solo, sin que el usuario tenga que pedirlo. Y se
        // guarda, para que salir de la pantalla y volver no lo borre (UX-002).
        const arrancado = {
          segundos: actual.prescripcion.restSeconds,
          desde: Date.now(),
          setId: set.id,
        };
        setDescanso(arrancado);
        void guardarDescanso({ ...arrancado, workoutLogId: log.id }).catch((caught: unknown) => {
          // No se pinta: el descanso funciona igual en esta pantalla. Lo unico
          // que se pierde es sobrevivir a salir y volver, y avisar de eso
          // mientras se entrena seria ruido.
          logger.warn('db', 'REST_PERSIST_FAILED', 'No se pudo guardar el descanso activo', {
            mensaje: detalleDeError(caught),
          });
        });

        if (recomendacion) {
          await persistRecommendation(ctx, {
            workoutLog: log,
            sessionExerciseId: actual.item.sessionExercise.id,
            setIndex: actual.prescripcion.setIndex,
            recommendation: recomendacion,
            strategy: String(recomendacion.inputsSnapshot['strategy'] ?? 'intra_session'),
          });
        }

        await refreshPending();
      } catch (caught) {
        // Tres cosas distintas, tres tratos distintos. Meterlas todas en
        // «entrada inválida» manda a corregir un número que estaba bien; y
        // volcar el `message` de un `ZodError` es el JSON crudo de UX-001.
        const deValidacion = traducirErrorDeValidacion(caught);
        if (deValidacion !== null) {
          logger.warn('ui', 'SET_REJECTED_BY_CONTRACT', 'El contrato rechazó la serie', {
            campos: camposConProblema(caught),
          });
          setError(deValidacion);
        } else {
          // Fallo de escritura o inesperado: el motivo VIAJA con el resultado.
          // En un móvil no hay consola que abrir, y «no se ha podido guardar» a
          // secas es el informe imposible que este repositorio ya documenta.
          const mensaje = mensajeDeError(caught);
          logger.error('db', 'SET_SAVE_FAILED', 'No se pudo guardar la serie', {
            mensaje: detalleDeError(caught),
          });
          setError(`No he podido guardar la serie: ${mensaje}`);
        }
      } finally {
        setGuardando(false);
      }
    })();
  }, [ctx, actual, workoutLogId, detalle, guardando, carga, reps, rir, recomendacion, refreshPending]);

  /*
   * Al entrar, se recupera el descanso que estuviera corriendo (UX-002).
   *
   * Vivia solo en el estado de React, asi que salir de la sesion —mirar el
   * plan, o que Android descargue la pestaña— lo borraba, y al volver no habia
   * ni cuenta atras ni forma de saber cuanto llevabas parado. Entre series eso
   * es la unica informacion que hay en pantalla.
   *
   * Solo si no hay uno ya en marcha: quien acaba de registrar una serie manda
   * sobre lo que hubiera guardado.
   */
  useEffect(() => {
    if (workoutLogId === null) return;
    let vigente = true;

    void leerDescanso(workoutLogId)
      .then((guardado) => {
        if (!vigente || guardado === null) return;
        setDescanso((previo) =>
          previo ?? {
            segundos: guardado.segundos,
            desde: guardado.desde,
            setId: guardado.setId,
          },
        );
      })
      .catch((caught: unknown) => {
        logger.warn('db', 'REST_RESTORE_FAILED', 'No se pudo recuperar el descanso activo', {
          mensaje: detalleDeError(caught),
        });
      });

    return () => {
      vigente = false;
    };
  }, [workoutLogId]);

  /*
   * El aviso del fin del descanso se PROGRAMA al empezarlo (UX-002).
   *
   * Antes se disparaba cuando el temporizador de la pantalla llegaba a cero, o
   * sea que dependia de que la pagina siguiera viva. Con la pantalla apagada
   * Android frena o suspende los temporizadores de JavaScript, asi que el aviso
   * llegaba tarde o no llegaba — justo en el caso para el que existe, porque
   * con la aplicacion delante ya se ve la cuenta atras y ya vibra.
   *
   * `programado` guarda si se pudo. En Windows la PWA no puede programar nada
   * sin un servicio de push, y ahi hace falta seguir avisando en el momento;
   * usar las dos vias siempre daria dos avisos en Android.
   *
   * La limpieza cancela: se ejecuta cuando arranca OTRO descanso —o sea cuando
   * se registra la serie siguiente antes de tiempo— y al salir de la sesion. Un
   * aviso que llega cuando ya te has vuelto a levantar es peor que ninguno.
   * Cuando el descanso termina solo no se limpia nada, que es lo que deja que
   * el sistema lo entregue a su hora.
   */
  const avisoProgramado = useRef(false);

  useEffect(() => {
    if (!ctx || !descanso) return;
    let vigente = true;

    void (async () => {
      await cancelarFinDeDescanso();
      const ok = await programarFinDeDescanso(
        ctx.userId,
        new Date(descanso.desde + descanso.segundos * 1000),
      );
      if (vigente) avisoProgramado.current = ok;
    })().catch((caught: unknown) => {
      logger.error('ui', 'REST_NOTICE_SCHEDULE_FAILED', 'No se pudo programar el fin del descanso', {
        mensaje: detalleDeError(caught),
      });
    });

    return () => {
      vigente = false;
      avisoProgramado.current = false;
      void cancelarFinDeDescanso();
    };
  }, [ctx, descanso]);

  const finDescanso = useCallback(
    (transcurridos: number) => {
      if (!ctx || !descanso || !workoutLogId) return;

      // Las dos siguen sin esperarse —descansar no puede quedarse bloqueado
      // porque una escritura tarde— pero ya no se pierden en silencio: un
      // `void` sin `catch` se traga el rechazo, que es la trampa que este mismo
      // repositorio tiene documentada (ERR-001).
      void logRestEvent(ctx, {
        workoutLogId,
        afterPerformedSetId: descanso.setId,
        plannedSeconds: descanso.segundos,
        actualSeconds: transcurridos,
        startedAt: new Date(descanso.desde).toISOString(),
      }).catch((caught: unknown) => {
        const mensaje = detalleDeError(caught);
        logger.error('db', 'REST_EVENT_FAILED', 'No se pudo registrar el descanso', { mensaje });
        setError('No he podido guardar este descanso. La serie sí está registrada.');
      });

      // El aviso inmediato solo si NO se pudo programar (UX-002). Donde se
      // programa, el sistema ya lo va a entregar a su hora y hacerlo aqui
      // tambien daria dos. Donde no —Windows, que no puede sin push— este es el
      // unico que hay, y solo funciona con la aplicacion delante.
      //
      // Y el fallo se PINTA, no solo se registra: esto depende de permisos de
      // notificacion, que es justo lo que no se puede depurar desde un movil.
      if (avisoProgramado.current) return;
      void avisarFinDeDescanso(ctx.userId).catch((caught: unknown) => {
        const mensaje = detalleDeError(caught);
        logger.error('ui', 'REST_NOTICE_FAILED', 'No se pudo avisar del fin del descanso', {
          mensaje,
        });
        setError('No he podido avisarte del fin del descanso. Revisa los permisos de avisos.');
      });
    },
    [ctx, descanso, workoutLogId],
  );

  if (!ready || detalle === undefined) {
    return <Estado tipo="cargando" titulo="Preparando la sesión" />;
  }
  if (detalle === null) {
    return (
      <Estado
        tipo="error"
        titulo="No se encuentra la sesión"
        accion={
          <BotonEnlace a="/entrenar" variante="secundario">Volver</BotonEnlace>
        }
      />
    );
  }

  // Todas las series registradas: toca cerrar.
  if (posicion === null) {
    return (
      <Estado
        tipo="vacio"
        titulo="Sesión completada"
        descripcion="Queda una valoración de dos preguntas."
        accion={
          <Boton onClick={() => navigate(`/entrenar/sesion/${detalle.session.id}/cierre`)}>
            Terminar sesión
          </Boton>
        }
      />
    );
  }

  if (!actual) return <Estado tipo="cargando" titulo="Preparando la serie" />;

  const ejercicio = requireExercise(actual.item.sessionExercise.exerciseSlug);
  const totalSeries = actual.item.prescriptions.length;
  const seriesTotalesSesion = detalle.exercises.reduce(
    (total, item) => total + item.prescriptions.length,
    0,
  );
  const progreso = (hechas.length / seriesTotalesSesion) * 100;

  // Una bandera roja que afecta a este ejercicio detiene la prescripcion y el
  // registro. La sesion no se cierra sola: salir es decision del usuario.
  const bloqueadoPorSeguridad =
    recomendacion?.reasonCodes.includes('halted_by_red_flag') ?? false;

  // Una POSIBLE URGENCIA no convive con una sesión: ni ejercicio en pantalla ni
  // campos para registrar, solo qué hacer (encargo del 23/09/2026, §6 y §8).
  // Readiness ya no deja pasar aquí con una urgencia; esto cubre entrar por
  // otra puerta —la sesión ya abierta, un enlace, otro dispositivo—.
  if (seguridad?.urgent === true) {
    return (
      <>
        <Cabecera titulo="Hoy no se entrena" />
        <section className="aviso-seguridad" role="alert">
          {seguridad.flags
            .filter((bandera) => bandera.urgent)
            .map((bandera) => (
              <p key={bandera.code} className="aviso-seguridad__texto">
                {bandera.message}
              </p>
            ))}
        </section>
      </>
    );
  }

  // Una máquina con equipamiento asignado y CERO cargas seleccionables. No es
  // lo mismo que un ejercicio de peso corporal, que no tiene ninguna a
  // propósito: por eso se exige además que haya `equipmentId`.
  const maquinaSinCargas =
    actual.item.sessionExercise.equipmentId !== null && cargasDisponibles.length === 0;

  // La serie de referencia de la última sesión. `sesionAnterior` ya descarta
  // las sesiones sin series y la de hoy; el `?? null` es para el tipo.
  const ultimaVez = recomendacion?.previousSession?.sets[0] ?? null;

  return (
    <>
      <header className="sesion-cabecera">
        {/* Un enlace, no un `<a>` con un `<button>` dentro: eran dos paradas de
            tabulador y HTML invalido. El aspa se dibuja, no se escribe: «✕» es
            un caracter y lo pinta la fuente del sistema, distinto en cada
            plataforma. */}
        <Link
          to={`/entrenar/sesion/${detalle.session.id}`}
          className="sesion-cabecera__salir"
          aria-label="Salir del entrenamiento"
        >
          <IconoCerrar />
        </Link>
        <span className="sesion-cabecera__posicion">
          Ejercicio {posicion.indiceEjercicio + 1} de {detalle.exercises.length}
        </span>
      </header>

      <div className="sesion-progreso" aria-hidden="true">
        <div className="sesion-progreso__barra" style={{ width: `${progreso}%` }} />
      </div>

      {yaHaEntrenado === false && <GuiaPrimeraSesion />}

      {bloqueadoPorSeguridad && (
        <section className="aviso-seguridad" role="alert">
          <p className="aviso-seguridad__titulo">Entrenamiento detenido</p>
          {(seguridad?.flags ?? [])
            .filter((bandera) => bandera.severity === 'halt')
            .map((bandera) => (
              <p key={bandera.code} className="aviso-seguridad__texto">
                {bandera.message}
              </p>
            ))}
          <p className="aviso-seguridad__texto">
            No voy a proponerte cargas mientras esto siga marcado. Puedes salir de la sesión sin
            registrar nada.
          </p>
        </section>
      )}

      <div className="ejercicio-actual">
        <p className="ejercicio-actual__nombre">{ejercicio.nameEs}</p>
        <p className="ejercicio-actual__meta">
          Serie {actual.prescripcion.setIndex} de {totalSeries} · objetivo{' '}
          {actual.prescripcion.targetRepsMin}-{actual.prescripcion.targetRepsMax} @ RIR{' '}
          {actual.prescripcion.targetRir}
        </p>
        {/* Una serie que no es normal tiene que decir COMO se hace, aqui y
            ahora. Prescribir un drop set y enseñarlo igual que los demas es
            como se consigue que se registre como una serie normal y que el
            volumen que cuenta el §4 no se parezca al que se hizo. */}
        {COMO_SE_HACE[actual.prescripcion.setType] !== null && (
          <p className="ejercicio-actual__tecnica" role="note">
            {COMO_SE_HACE[actual.prescripcion.setType]}
          </p>
        )}
      </div>

      {calentamiento.length > 0 && (
        <div className="calentamiento">
          <button
            type="button"
            className="boton boton--texto calentamiento__interruptor"
            aria-expanded={calentamientoAbierto}
            onClick={() => setCalentamientoAbierto((abierto) => !abierto)}
          >
            <IconoPunta
              className={`calentamiento__punta${
                calentamientoAbierto ? ' calentamiento__punta--abierta' : ''
              }`}
            />
            Series de aproximación ({calentamiento.length})
          </button>
          {calentamientoAbierto && (
            <>
              {calentamiento.map((serie) => (
                <div key={serie.clave} className="calentamiento__serie">
                  <span className="numeros">
                    {formatKg(serie.loadKg)} × {serie.reps}
                  </span>
                  <Boton
                    variante="texto"
                    disabled={serie.fila === null || serie.completada}
                    onClick={() => {
                      if (!ctx || serie.fila === null) return;
                      setErrorCalentamiento(null);
                      void markWarmupCompleted(ctx, serie.fila)
                        .then(refreshPending)
                        .catch((caught: unknown) => {
                          const mensaje = detalleDeError(caught);
                          logger.error('db', 'WARMUP_MARK_FAILED', 'No se pudo marcar la aproximacion', {
                            mensaje,
                          });
                          setErrorCalentamiento('No he podido marcar esta aproximación.');
                        });
                    }}
                  >
                    {serie.completada ? '✓ Hecha' : 'Marcar'}
                  </Boton>
                </div>
              ))}
              {errorCalentamiento !== null && (
                <p className="campo__error" role="alert">
                  {errorCalentamiento}
                </p>
              )}
              <p className="calentamiento__aviso">No cuenta como serie efectiva.</p>
            </>
          )}
        </div>
      )}

      <div className="recomendacion">
        <p className="recomendacion__etiqueta">Recomendado</p>
        {/* El tamaño depende de si hay numero. Con «Elige tú la carga» pintado
            a 32 px, la frase que dice que NO hay recomendacion se leia mas
            fuerte que cualquier recomendacion de verdad. */}
        <p
          className={`recomendacion__valor${
            recomendacion?.loadKg == null ? ' recomendacion__valor--sin-numero' : ''
          }`}
        >
          {recomendacion?.loadKg !== null && recomendacion !== null
            ? formatKg(recomendacion.loadKg as number)
            : 'Elige tú la carga'}
        </p>
        {/*
          Dos motivos distintos para no proponer carga, y hasta el 06/09/2026
          los dos daban el MISMO consejo: «primera vez con este ejercicio». Es
          R-24 otra vez —un consejo pegado a un mensaje sin mirar la causa— y
          esta vez costó datos. Con el equipamiento sin cargas, la pantalla
          decía «elige tú» sin mencionar que la máquina estaba vacía; se
          escribía un número cualquiera, esa serie entraba en el historial y
          desde entonces el motor recomendaba ese número, fielmente, para
          siempre. El aviso correcto SÍ existía —`equipment_not_configured` en
          `porQue.ts`— pero vivía dentro del panel «¿Por qué?», que arranca
          cerrado y está debajo del botón de guardar.

          Y no se mira `equipment_not_configured`, aunque exista: el motor
          comprueba «primera vez» ANTES que las cargas, así que en la primera
          sesión con un ejercicio —justo cuando se hace el daño— ese código no
          llega nunca. La pantalla no lo necesita: ya ha leído las cargas de esa
          máquina unas líneas más arriba.
        */}
        {recomendacion?.loadKg === null &&
          (maquinaSinCargas ? (
            <p className="recomendacion__nota" role="note">
              Esta máquina <strong>no tiene cargas configuradas</strong>, así que no puedo
              proponerte un peso ni comprobar que el que escribas exista de verdad. Lo que
              registres aquí será lo que te recomiende a partir de ahora, así que anota el peso
              real. Se arregla en <Link to="/mas/gimnasio">Más → Gimnasio</Link>.
            </p>
          ) : (
            <p className="recomendacion__nota">
              Primera vez con este ejercicio: usa una carga que te deje a RIR{' '}
              {actual.prescripcion.targetRir}.
            </p>
          ))}
      </div>

      <div className="accion-serie">
        {/*
          «¿Qué hice la última vez?», encima de los campos y sin salir de aquí
          (UX-003). El dato llevaba desde siempre cargándose para el motor y se
          tiraba: la pantalla solo enseñaba las series de HOY, que es justo lo
          que el usuario ya recuerda.

          Se pinta la PRIMERA serie de esa sesión, no la mejor. Es la que el
          propio motor usa como referencia entre sesiones, y buscar «la mejor»
          sería inventar un récord por la puerta de atrás, que es TRAIN-002 y no
          toca. Si no hay sesión anterior no se escribe nada: el bloque de
          recomendación ya dice «primera vez con este ejercicio», y repetirlo
          aquí sería ruido.
        */}
        {ultimaVez !== null && (
          <p className="registro__ultima-vez">
            Última vez:{' '}
            <span className="numeros">
              {formatKg(ultimaVez.loadKg)} × {ultimaVez.reps} · RIR {ultimaVez.rir}
            </span>
          </p>
        )}

        <div className="registro">
          <CampoRegistro
            etiqueta="Carga (kg)"
            valor={carga}
            onChange={setCarga}
            paso={LIMITES_SERIE.carga.paso}
            min={LIMITES_SERIE.carga.min}
            max={LIMITES_SERIE.carga.max}
            decimal
            ancho
          />
          <CampoRegistro
            etiqueta="Reps"
            valor={reps}
            onChange={setReps}
            paso={LIMITES_SERIE.reps.paso}
            min={LIMITES_SERIE.reps.min}
            max={LIMITES_SERIE.reps.max}
          />
          <CampoRegistro
            etiqueta="RIR"
            valor={rir}
            onChange={setRir}
            paso={LIMITES_SERIE.rir.paso}
            min={LIMITES_SERIE.rir.min}
            max={LIMITES_SERIE.rir.max}
            decimal
          />
        </div>

        <Boton
          ancho="completo"
          onClick={guardarSerie}
          cargando={guardando}
          disabled={bloqueadoPorSeguridad}
        >
          {guardando ? 'Guardando…' : 'Guardar serie'}
        </Boton>

        {error !== null && (
          <p className="campo__error" role="alert">
            {error}
          </p>
        )}
      </div>

      {/*
        Las acciones secundarias del §4.7, en UNA fila: «¿Por qué? · Sustituir
        ejercicio». Debajo de «Guardar serie» y en variante de texto, porque
        ninguna de las dos compite con registrar la serie. Van juntas porque son
        lo mismo desde el punto de vista de quien entrena: las dos preguntas que
        se le hacen a la maquina cuando algo no cuadra.
      */}
      <div className="acciones-ejercicio">
        {recomendacion && (
          <Boton
            variante="texto"
            aria-expanded={porQueAbierto}
            onClick={() => {
              setPorQueAbierto((abierto) => !abierto);
              setSustituyendo(false);
            }}
          >
            ¿Por qué?
          </Boton>
        )}
        <Boton
          variante="texto"
          aria-expanded={sustituyendo}
          onClick={() => {
            setSustituyendo((abierto) => !abierto);
            setPorQueAbierto(false);
          }}
        >
          <IconoSustituir tam={16} />
          Sustituir ejercicio
        </Boton>
      </div>

      {recomendacion && porQueAbierto && (
        <PorQuePanel
          recomendacion={recomendacion}
          ejercicio={ejercicio.nameEs}
          serie={actual.prescripcion.setIndex}
          totalSeries={totalSeries}
          onCerrar={() => setPorQueAbierto(false)}
        />
      )}

      {/*
        Sustituir DURANTE la sesion (docs/TRAINING_ENGINE.md §7.2: «disponible en
        previsualizacion y durante la sesion»). Es la mitad del §8.2 que no se
        habia construido, y es la que se usa: el motivo por el que hace falta
        cambiar un ejercicio —la maquina ocupada, el banco roto— no se sabe
        hasta estar delante de ella.

        Se ofrece tambien con el entrenamiento detenido por una bandera roja: ahi
        es justo lo que hay que poder hacer, y quien decide si la alternativa se
        puede prescribir sigue siendo el motor, que mira el perfil de CADA
        ejercicio. Esconder el boton no protegeria a nadie, dejaria al usuario
        sin salida.
      */}
      {sustituyendo && (
        <SustituirEjercicio
          sessionExercise={actual.item.sessionExercise}
          {...(checkin === null ? {} : { readinessScore: checkin.readinessScore })}
          seriesRegistradas={seriesDelHuecoHoy.length}
          onHecho={() => {
            setSustituyendo(false);
            void recargarDetalle();
          }}
        />
      )}

      {/*
        Se pintan las del HUECO, no las del ejercicio actual. Tras sustituir a
        mitad, en el hueco hay series de dos ejercicios distintos y las dos se
        hicieron: quitar las viejas seria esconder trabajo real. La que no es
        del ejercicio de ahora dice de cual es, porque «Serie 1 · 72,5 kg»
        debajo de otro nombre se lee como carga de este.
      */}
      {seriesDelHuecoHoy.length > 0 && (
        <div className="series-hechas">
          {seriesDelHuecoHoy.map((set) => {
            const deOtro = set.exerciseSlug !== actual.item.sessionExercise.exerciseSlug;
            return (
              <div key={set.id} className="serie-hecha">
                <span className="serie-hecha__indice">
                  Serie {set.setIndex}
                  {deOtro && (
                    <span className="serie-hecha__ejercicio">
                      {' '}
                      · {findExercise(set.exerciseSlug)?.nameEs ?? set.exerciseSlug}
                    </span>
                  )}
                </span>
                <span className="numeros">
                  {formatKg(set.loadKg)} × {set.reps} @ RIR {set.rir}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {descanso && (
        <Descanso
          segundos={descanso.segundos}
          iniciadoEn={descanso.desde}
          onFin={finDescanso}
        />
      )}
    </>
  );
}

/**
 * Campo numérico con pasos grandes: el objetivo es no tener que teclear.
 *
 * Los botones ± están ACOTADOS al rango del contrato (UX-001). Antes solo
 * tenían un `Math.max(0, …)`, así que treinta pulsaciones del «+» dejaban el
 * RIR en 19 y veinte del «−» dejaban las repeticiones en 0: dos valores que el
 * contrato rechaza y que solo se descubrían al guardar.
 *
 * El campo sigue siendo de texto y no `type="number"` a propósito: con
 * `inputMode="decimal"` el teclado de Android en España ofrece la COMA, y un
 * `input[type=number]` la descarta. Por eso los topes viajan como `min`/`max`/
 * `step` y, para que además signifiquen algo, como `role="spinbutton"` con sus
 * `aria-value*`: en un campo de texto los atributos sueltos son decorativos.
 */
function CampoRegistro({
  etiqueta,
  valor,
  onChange,
  paso,
  min,
  max,
  decimal = false,
  ancho = false,
}: {
  etiqueta: string;
  valor: string;
  onChange: (valor: string) => void;
  paso: number;
  min: number;
  max: number;
  decimal?: boolean;
  /** Ocupa la fila entera: para el campo que necesita mas digitos. */
  ancho?: boolean;
}): ReactNode {
  const numero = Number(valor.replace(',', '.'));
  const valido = valor.trim() !== '' && Number.isFinite(numero);

  const ajustar = (delta: number): void => {
    const base = valido ? numero : min;
    const siguiente = Math.min(max, Math.max(min, base + delta));
    onChange(decimal ? String(Number(siguiente.toFixed(2))) : String(Math.round(siguiente)));
  };

  return (
    <div className={`registro__campo${ancho ? ' registro__campo--ancho' : ''}`}>
      <span className="registro__etiqueta">{etiqueta}</span>
      <div className="registro__control">
        <button
          type="button"
          className="registro__paso"
          aria-label={`${etiqueta}: restar ${paso}`}
          disabled={valido && numero <= min}
          onClick={() => ajustar(-paso)}
        >
          −
        </button>
        <input
          className="registro__input"
          inputMode={decimal ? 'decimal' : 'numeric'}
          value={valor}
          aria-label={etiqueta}
          role="spinbutton"
          min={min}
          max={max}
          step={paso}
          aria-valuemin={min}
          aria-valuemax={max}
          {...(valido ? { 'aria-valuenow': numero } : {})}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="registro__paso"
          aria-label={`${etiqueta}: sumar ${paso}`}
          disabled={valido && numero >= max}
          onClick={() => ajustar(paso)}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * Lectura puntual del registro para no depender del estado de React.
 *
 * El `import()` dinamico que habia aqui no separaba nada: `db.ts` lo importan
 * estaticamente el `DataProvider` y trece repositorios mas, asi que el modulo
 * viajaba igual en el paquete principal y el build lo decia en cada
 * compilacion —`INEFFECTIVE_DYNAMIC_IMPORT`—. Al dividir por rutas (PERF-002)
 * lo unico que conseguia era ensuciar el aviso del build sin ahorrar un byte.
 */
async function ctxWorkoutLog(workoutLogId: string): Promise<WorkoutLog | undefined> {
  return db.workoutLogs.get(workoutLogId);
}
